import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, refreshSession } from "@/lib/api";
import { isNative } from "@/lib/native";
import { saveTokens, clearTokens, getRefresh, getBioPref, biometricUnlock } from "@/lib/tokens";
import { unregisterPush } from "@/lib/push";

const AuthContext = createContext(null);

async function restoreNativeSession() {
  if (!(await getRefresh())) return false;
  if ((await getBioPref()) && !(await biometricUnlock())) return false;
  try { await refreshSession(); return true; } catch { await clearTokens(); return false; }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return false;
    }
  }, []);

  useEffect(() => {
    (async () => {
      if (isNative && !(await restoreNativeSession())) return setUser(false);
      refresh();
    })();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    await saveTokens(data);
    setUser(data);
    return data;
  };
  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    await saveTokens(data);
    setUser(data);
    return data;
  };
  const logout = async () => {
    await unregisterPush();
    await api.post("/auth/logout").catch(() => {});
    await clearTokens();
    setUser(false);
  };

  return <AuthContext.Provider value={{ user, setUser, login, register, logout, refresh }}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
